import ReactModal from 'react-modal';
import './Modal.scss';
import {ReactComponent as DeleteIcon} from '../../images/nde-icon-delete.svg';
import { useSelector } from 'react-redux';
import { selectControls } from '../../redux/controls';
import { useEffect, useRef, useState, useCallback } from 'react';
import { ModalProps } from './models/Modal.interfaces';
import { WindowType } from './models/Modal.types';
import { onAfterOpenCallback } from './models/Modal.utils';

function Modal({ bodyText, headerText, open, onClose, shouldReturnFocusAfterClose, windowType, multiLineHeaderText }: ModalProps): JSX.Element {
  const controls = useSelector(selectControls);
  ReactModal.setAppElement('#root');

  const cancelEl = useRef<HTMLButtonElement>(null);
  const okEl = useRef<HTMLButtonElement>(null);
  const [returnFocusAfterClose, setReturnFocusAfterClose] = useState<boolean>(true);
  const [contentLabel, setContentLabel] = useState<string>(headerText ? headerText : 'Modal Popup');
  const [winType, setWinType] = useState<WindowType>('DESC');
  const [warningFourPartA, setWarningFourPartA] = useState<string>('');
  const [warningFourPartB, setWarningFourPartB] = useState<string>('');
  const [warningFourPartC, setWarningFourPartC] = useState<string>('');
  const [warningFourExists, setWarningFourExists] = useState<boolean>(false);
  const [warningFourDoesNotExist, setWarningFourDoesNotExist] = useState<boolean>(true);

  useEffect(() => {
    if (shouldReturnFocusAfterClose === undefined) {
      setReturnFocusAfterClose(true);
    } else {
      setReturnFocusAfterClose(shouldReturnFocusAfterClose);
    }
  }, [shouldReturnFocusAfterClose]);

  useEffect(() => {
    if (headerText) {
      setContentLabel(headerText);
    } else {
      setContentLabel(controls ? controls?.ModalDefaultTitle?.controlText : '');
    }
  }, [controls, headerText]);

  useEffect(() => {
    if (windowType) {
      setWinType(windowType);
    } else {
      setWinType('DESC');
    }
  }, [windowType]);

  useEffect(() => {

    let warningMessage = false;
    if(multiLineHeaderText){
      warningMessage = true;
    }
   
    if (winType === 'PROMPT' && warningMessage && bodyText) {
      setWarningFourExists(bodyText.includes('casefour'));
      if (warningFourExists) {
        setWarningFourDoesNotExist(false);
        const WarningCaseFourArray = bodyText.split(',');
        setWarningFourPartA(WarningCaseFourArray[0]);
        setWarningFourPartB(WarningCaseFourArray[1]);
        setWarningFourPartC(WarningCaseFourArray[2]);
      }
    }
  }, [winType, onClose, multiLineHeaderText, bodyText, warningFourExists]);



  const resetItemSelectionsInListView = useCallback(() => {
    // reset selections by simply clicking the select all checkbox and then clicking it again if the beginning state had less than all items selected
    const selectAllItemsCheckBox = document.getElementById('table-header--select-all');
    if (selectAllItemsCheckBox) {
      selectAllItemsCheckBox.click();
    }

    const allSelected = Array.from(document.querySelectorAll('div.nqt-checkbox-container.tab-data.selected'));
    const allTabData = Array.from(document.querySelectorAll('div.nqt-checkbox-container.tab-data'));
    const allTabItemsInListView = [];
    const selectedTabItems = allSelected.slice(1, allSelected.length);

    allSelected.forEach((item,index) => {
      if (index > 0) {
        allTabItemsInListView.push(index);
      }
      if(allTabItemsInListView.length === selectedTabItems.length){
        onClose(false);
        if (allTabData.length -1 !== allSelected.length - 1) {
          setTimeout(() => {
            if (selectAllItemsCheckBox) {
              selectAllItemsCheckBox.click();
            }
          }, 300);
        }
      } 
    });
    
  },[onClose]);


  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          onAfterOpen={() => onAfterOpenCallback(winType, cancelEl, okEl)}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          shouldReturnFocusAfterClose={returnFocusAfterClose}
          contentLabel={contentLabel}
        >
          <div className='nqt-modal--container'>
            <div className={`${multiLineHeaderText ? 'nqt-modal--header-group-multi' : 'nqt-modal--header-group'}`}>
              <header className='nqt-modal--title'>{
                headerText && multiLineHeaderText ? 
                  <span dangerouslySetInnerHTML={{__html: headerText}}></span> : headerText && headerText
              }</header>
              <button className='nqt-modal--close-button'
                ref={(el) => el?.focus()}
                onClick={() => onClose(false)}>
                <DeleteIcon/>
                <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
              </button>
            </div>
            <div className="nqt-modal--border">
              <section className="nqt-modal--content">
                {
                  warningFourDoesNotExist &&
                    <>
                      <span className="nqt-modal--body" dangerouslySetInnerHTML={{__html: bodyText ? bodyText:''}}></span>
                    </>
                }
                {
                  warningFourExists ?

                    <div className='nqt-modal-warning--case'>
                      <div className="nqt-modal-warning--case-header" dangerouslySetInnerHTML={{__html: warningFourPartA}}></div>
                      <div className="nqt-modal-warning--button-container">
                        <button className="nqt-modal--buttons-warning"
                          onClick={() => onClose(true)}>{controls?.ModalCaseFourWarningOk?.controlText}</button>
                        <span>OR</span>
                        <button className="nqt-modal--buttons-warning"
                          onClick={() => resetItemSelectionsInListView()}>{controls?.ModalCaseFourWarningCancel?.controlText}</button>
                      </div> 
                      <div className='nqt-modal-warning--case'>
                        <div className="nqt-modal--tip" dangerouslySetInnerHTML={{__html: warningFourPartB}}></div>
                        <div className="nqt-modal--tip-image" dangerouslySetInnerHTML={{__html: warningFourPartC}}></div>
                      </div>
                    </div>
                    : <></>
                }
              </section>
              {
                warningFourExists ? 
                  <></>: 
                  winType === 'PROMPT' &&  
                  <>
                    <hr />
                    <div className="nqt-modal--buttons col-7 offset-5">
                      <button className="nqt-button--cancel col-5"
                        onClick={() => onClose(false)}>{controls?.ModalCancel?.controlText}</button>
                      <button className="nqt-button--submit col-5"
                        ref={okEl}
                        onClick={() => onClose(true)}>{controls?.ModalOK?.controlText}</button>
                    </div>
                  </> 
                 
              }
              
            </div>
          </div>
        </ReactModal>
      }
    </>
  );
}

export default Modal;